import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form';

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const loginSchema = z.object({
    email: z
      .string()
      .email('Please enter a valid email address.'),
    password: z
      .string()
      .min(6, 'Password must be at least 6 characters long.'),
  });

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      await login(values.email, values.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div
        className="login-image"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1674207219163-f1489b22b8b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMGNhcmRib2FyZCUyMGJveGVzJTIwc3RhY2slMjBtaW5pbWFsaXN0JTIwYmx1ZSUyMHNoaXBwaW5nJTIwY29udGFpbmVyJTIwdGV4dHVyZXxlbnwwfHx8fDE3NjUzNjEzMDV8MA&ixlib=rb-4.1.0&q=85)',
        }}
      />
      <div className="login-form-container">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="login-form"
            data-testid="login-form"
          >
            <h1 className="login-logo">GAUSS</h1>
            <p className="login-subtitle">Sign in to your account</p>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel asChild>
                    <Label htmlFor="email">Email</Label>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      data-testid="login-email-input"
                      autoComplete="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel asChild>
                    <Label htmlFor="password">Password</Label>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      data-testid="login-password-input"
                      autoComplete="current-password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full h-11"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>

            <p className="text-center text-sm text-slate-500 mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-slate-900 font-medium hover:underline" data-testid="register-link">
                Create one
              </Link>
            </p>
          </form>
        </Form>
      </div>
    </div>
  );
};
